import { Loading3QuartersOutlined, WarningFilled } from '@ant-design/icons'
import { Form, Input, InputNumber, Switch, DatePicker, Modal } from 'antd'
import * as React from 'react'
import { useState, useEffect } from 'react'
import { useMountedRef } from '@halo/hooks/use-mounted-ref'
import { AccountService } from '@halo/services/account.service'
import { Container } from 'typescript-ioc/es5'
import { classnames } from '@halo/_utils/classnames'
import { SUPPORTED_BILLING_CURRENCIES } from '@halo/constants'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { numberAsCurrency, numberWithCommas } from '@halo/_utils/numeral'
import './account-charge-form.scss'
import moment, { Moment } from 'moment-timezone'
import { isDef } from '@pushly/aqe/lib/utils/is-def'
import { Drawer, Well } from '@pushly/aqe/lib/components'

interface IAccountBillingEditor {
    account: any
    visible: boolean
    onClose: (e: any) => void
    onSuccess: (charge: any) => void
    defaultBillingPeriod?: string
}

export const AccountChargeForm: React.FC<IAccountBillingEditor> = (props) => {
    const [form] = Form.useForm()
    const [overrideEnabled, setOverrideEnabled] = useState(false)
    const [loadingSelectedBillablePeriodSubs, setLoadingSelectedBillablePeriodSubs] = useState(false)
    const [selectedBillablePeriodSubs, setSelectedBillablePeriodSubs] = useState<number | undefined>(undefined)
    const [selectedBillablePeriodAlreadyBilled, setSelectedBillablePeriodAlreadyBilled] = useState(false)
    const [accountBillingRecords, setAccountBillingRecords] = useState<any[]>([])
    const [lastBillingRecord, setLastBillingRecord] = useState<any>(null)
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

    const [mountedRef, runIfMounted] = useMountedRef()
    const accountService = Container.get(AccountService)

    // Equivalent to componentDidMount and componentDidUpdate
    useEffect(() => {
        if (props.visible) {
            ensureRequiredDependencies()
        }
    }, [props.account, props.visible])

    // Cleanup timeout and modals on unmount
    useEffect(() => {
        return () => {
            // Clear any pending timeouts
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }

            // Destroy any open modals to prevent memory leaks
            Modal.destroyAll()
        }
    }, [])

    // Getters converted to functions or useMemo hooks
    const account = props.account

    const propsValue = props.account?.billing_data || {}

    const lastMonthValue = moment().subtract(1, 'month').startOf('month')

    const currentValues = React.useMemo(() => {
        const formValue = form.getFieldsValue()

        let values: any = {
            type: (formValue.type || propsValue.type || 'INVOICE').toUpperCase(),
            currency: (formValue.currency || propsValue.currency || 'USD').toUpperCase(),
        }

        if (values.type === 'CREDIT') {
            values.rate = formValue.rate || propsValue.rate
        }

        if (propsValue.stripe_customer_id) {
            values.stripe_customer_id = propsValue.stripe_customer_id
            values.stripe_customer_email = propsValue.stripe_customer_email
            values.stripe_customer_last4 = propsValue.stripe_customer_last4
        }

        return values
    }, [propsValue, form])

    const currentTypeIsCredit = currentValues.type === 'CREDIT'

    const currentCurrencyDisplayName = SUPPORTED_BILLING_CURRENCIES.find(
        (cur) => cur.value === currentValues.currency,
    )!.label

    const currentCurrencySymbol = SUPPORTED_BILLING_CURRENCIES.find(
        (cur) => cur.value === currentValues.currency,
    )!.symbol

    const expectedChargeValue = React.useMemo(() => {
        let value: any
        const subs = selectedBillablePeriodSubs || 0

        if (subs > 0) {
            value = account.billing_data.rate

            if (account.billing_data.rate_type === 'VARIABLE_SUBSCRIBER') {
                value = subs * account.billing_data.rate
            }
        }

        return value
    }, [account, selectedBillablePeriodSubs])

    const lineItemDescription = `Pushly Monthly Fee - ${numberWithCommas(selectedBillablePeriodSubs)} Subscribers`

    // Methods converted to functions with useCallback
    const toggleOverrideEnabled = React.useCallback((enabled: boolean) => {
        setOverrideEnabled(enabled)
    }, [])

    const renderLastBilledAt = () => {
        if (!lastBillingRecord) {
            return '--'
        }

        return moment(lastBillingRecord.created_at).format('LL')
    }

    const renderExpectedCharge = () => {
        const symbol = currentCurrencySymbol
        const value: any = expectedChargeValue === undefined ? '--' : expectedChargeValue

        return !selectedBillablePeriodSubs ? '--' : `${symbol}${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    const clearForm = React.useCallback(async () => {
        form.resetFields(['override', 'overrideReason', 'notes'])

        runIfMounted(() => {
            setOverrideEnabled(false)
            setSelectedBillablePeriodSubs(undefined)
        })
    }, [form, runIfMounted])

    const handleSubmit = React.useCallback(async () => {
        try {
            const values = await form.validateFields()

            const recordDto: any = {
                billable_period: values.billablePeriod.format('MMMM, YYYY'),
                description: lineItemDescription,
                amount: expectedChargeValue,
                notes: values.notes,
            }

            if (values.override) {
                recordDto.amount = values.override
                recordDto.description = values.lidOverride
                recordDto.original_amount = expectedChargeValue
            }

            const record = await accountService.createBillingRecordForAccountId(account.id, recordDto)

            if (record.ok) {
                Modal.confirm({
                    title: 'Confirm Payment',
                    content: (
                        <div>
                            <p>Please confirm the following payment details for account: {props.account.name}</p>

                            <div className={classnames('form-layout-row')}>
                                <div className={classnames('form-layout-row-label')}>Billable Period</div>
                                <div className={classnames('form-layout-row-value')}>{recordDto.billable_period}</div>
                            </div>

                            <div className={classnames('form-layout-row')}>
                                <div className={classnames('form-layout-row-label')}>Billable Subs</div>
                                <div className={classnames('form-layout-row-value')}>
                                    {numberWithCommas(selectedBillablePeriodSubs)}
                                </div>
                            </div>

                            <div className={classnames('form-layout-row')}>
                                <div className={classnames('form-layout-row-label')}>Description</div>
                                <div className={classnames('form-layout-row-value')}>{recordDto.description}</div>
                            </div>

                            <div className={classnames('form-layout-row')}>
                                <div className={classnames('form-layout-row-label')}>Amount</div>
                                <div className={classnames('form-layout-row-value')}>
                                    {currentCurrencySymbol}
                                    {numberAsCurrency(recordDto.amount)}
                                </div>
                            </div>

                            <div className={classnames('form-layout-row')}>
                                <div className={classnames('form-layout-row-label')}>Notes</div>
                                <div className={classnames('form-layout-row-value')}>{recordDto.notes || 'None'}</div>
                            </div>
                        </div>
                    ),
                    onOk: async () => {
                        const confirmation = await accountService.confirmBillingRecord(
                            record.data.confirmation_endpoint,
                        )

                        const stripeError = confirmation.data?.metadata?.stripe_error

                        if (confirmation.ok) {
                            props.onClose(null)
                            if (stripeError) {
                                simpleNotification(
                                    'error',
                                    `The requested charge for, ${account.name}, has been declined with the following error: ${stripeError.message}`,
                                )
                            } else {
                                simpleNotification(
                                    'success',
                                    `Account, ${account.name}, has been successfully charged.`,
                                )
                            }
                        } else {
                            simpleNotification('error', 'An error occurred during payment processing.')
                        }

                        Modal.destroyAll()
                    },
                })
            } else {
                simpleNotification('error', 'An error occurred during payment record creation.')
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }, [form, lineItemDescription, expectedChargeValue, accountService, account, props.account?.name, props.onClose, selectedBillablePeriodSubs, currentCurrencySymbol])

    const handleClose = React.useCallback(async () => {
        await clearForm()
        props.onClose(null)
    }, [clearForm, props.onClose])

    const updateSelectedPeriodBillableSubs = React.useCallback(async (billablePeriod: Moment | undefined): Promise<void> => {
        runIfMounted(() => {
            setLoadingSelectedBillablePeriodSubs(true)
            setSelectedBillablePeriodAlreadyBilled(false)
        })

        if (accountBillingRecords.length > 0) {
            const existing = accountBillingRecords.find(
                (r: any) => r.metadata.billable_period === billablePeriod?.format('MMMM, YYYY'),
            )

            if (existing) {
                runIfMounted(() => {
                    setSelectedBillablePeriodAlreadyBilled(true)
                })
            }
        }

        if (mountedRef.current) {
            await clearForm()
        }

        runIfMounted(() => {
            setLoadingSelectedBillablePeriodSubs(false)
        })

        if (!billablePeriod || !mountedRef.current) {
            return
        }

        const dateRange = {
            since: billablePeriod.startOf('month').format('YYYY-MM-DD'),
            through: billablePeriod.endOf('month').format('YYYY-MM-DD'),
        }

        const { ok, data } = await accountService.fetchSubscriberHistoryByAccountId(
            props.account.id,
            { date_range: JSON.stringify(dateRange) },
            false,
            `charge-sub-history-${props.account.id}`,
        )

        if (ok && data.length > 0) {
            runIfMounted(() => {
                setSelectedBillablePeriodSubs(data[0].subscribed_count || 0)
            })
        }
    }, [accountBillingRecords, mountedRef, clearForm, runIfMounted, accountService, props.account?.id])

    const ensureRequiredDependencies = React.useCallback(async (): Promise<void> => {
        if (!props.account) {
            if (mountedRef.current) {
                // Clear any existing timeout
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current)
                }
                // Set new timeout and store the ID
                timeoutRef.current = setTimeout(() => ensureRequiredDependencies(), 50)
            }
            return
        }

        if (props.defaultBillingPeriod && mountedRef.current) {
            updateSelectedPeriodBillableSubs(moment(props.defaultBillingPeriod))
        }

        const { ok, data } = await accountService.fetchBillingRecordsByAccountId(props.account.id, {
            order: JSON.stringify({
                field: 'record.created_at',
                direction: 'DESC',
            }),
            processed: 1,
        })

        if (props.account && ok && data.length > 0) {
            runIfMounted(() => {
                setAccountBillingRecords(data)
                setLastBillingRecord(data[0])
            })
        }
    }, [props.account, props.defaultBillingPeriod, mountedRef, timeoutRef, updateSelectedPeriodBillableSubs, accountService, runIfMounted])

    // Form layout
    const formItemLayout = {
        labelCol: {
            xs: { span: 24 },
            sm: { span: 6 },
        },
        wrapperCol: {
            xs: { span: 24 },
            sm: { span: 18 },
        },
    }

    return (
        <Drawer
            className={classnames('adf-billing-editor')}
            title={`Payment ${props.visible && `- ${props.account?.name}`}`}
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            submitText="Submit Payment"
            onClose={handleClose}
            visible={props.visible}
        >
            {props.visible && (
                <>
                    <Well title="Payment Profile" hideFooter={true}>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Customer Reference ID</div>
                            <div className={classnames('form-layout-row-value')}>
                                {currentValues.stripe_customer_id}
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Contact Email</div>
                            <div className={classnames('form-layout-row-value')}>
                                {currentValues.stripe_customer_email}
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Current Credit Card</div>
                            <div className={classnames('form-layout-row-value')}>
                                **** **** **** {currentValues.stripe_customer_last4}
                            </div>
                        </div>
                    </Well>

                    <Well hideHeader={true} hideFooter={true}>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Billing Type</div>
                            <div className={classnames('form-layout-row-value')}>
                                {currentTypeIsCredit ? 'Credit Card' : 'Invoicing'}
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Currency</div>
                            <div className={classnames('form-layout-row-value')}>{currentCurrencyDisplayName}</div>
                        </div>

                        {currentTypeIsCredit && (
                            <>
                                <div className={classnames('form-layout-row')}>
                                    <div className={classnames('form-layout-row-label')}>Rate</div>
                                    <div className={classnames('form-layout-row-value')}>
                                        {currentValues.rate_type === 'FLAT_RATE'
                                            ? 'Flat Monthly Rate'
                                            : 'Variable Subscriber Rate'}
                                    </div>
                                </div>

                                <div className={classnames('form-layout-row')}>
                                    <div className={classnames('form-layout-row-label')}>Value</div>
                                    <div className={classnames('form-layout-row-value')}>
                                        {currentCurrencySymbol}
                                        {currentValues.rate} /{' '}
                                        {currentValues.rate_type === 'FLAT_RATE' ? 'month' : 'subscriber'}
                                    </div>
                                </div>
                            </>
                        )}
                    </Well>

                    <Well hideHeader={true} hideFooter={true}>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Last Billed</div>
                            <div className={classnames('form-layout-row-value')}>{renderLastBilledAt()}</div>
                        </div>
                    </Well>

                    <Well title="Make a Charge" hideFooter={true}>
                        <Form form={form} className={classnames('acf-form', 'hide-inline-errors')} {...formItemLayout}>
                            <Form.Item
                                className={classnames('acf-form-billable-period')}
                                label="Billable Period"
                                name="billablePeriod"
                                initialValue={
                                    !props.defaultBillingPeriod ? undefined : moment(props.defaultBillingPeriod)
                                }
                                rules={[
                                    {
                                        required: true,
                                        message: 'You must select a billable period.',
                                    },
                                ]}
                            >
                                <DatePicker.MonthPicker
                                    placeholder=""
                                    format="MMMM, YYYY"
                                    disabledDate={(current: Moment) => {
                                        return current.startOf('month') > lastMonthValue
                                    }}
                                    onChange={updateSelectedPeriodBillableSubs}
                                />
                            </Form.Item>

                            {selectedBillablePeriodAlreadyBilled && (
                                <div className={classnames('acf-form-billable-period-error')}>
                                    <WarningFilled />
                                    <span>Selected period has already been billed.</span>
                                </div>
                            )}

                            {isDef(form.getFieldValue('billablePeriod')) && (
                                <>
                                    <Form.Item className={classnames('acf-form-calc-amount')} label="Billable Subs">
                                        {loadingSelectedBillablePeriodSubs ? (
                                            <Loading3QuartersOutlined spin={true} />
                                        ) : (
                                            numberWithCommas(selectedBillablePeriodSubs)
                                        )}
                                    </Form.Item>
                                    <Form.Item className={classnames('acf-form-calc-amount')} label="Calculated Amount">
                                        {loadingSelectedBillablePeriodSubs ? (
                                            <Loading3QuartersOutlined spin={true} />
                                        ) : (
                                            renderExpectedCharge()
                                        )}
                                    </Form.Item>
                                    <Form.Item className={classnames('acf-form-lid')} label="Description">
                                        {loadingSelectedBillablePeriodSubs ? (
                                            <Loading3QuartersOutlined spin={true} />
                                        ) : (
                                            lineItemDescription
                                        )}
                                    </Form.Item>

                                    {!loadingSelectedBillablePeriodSubs && (
                                        <>
                                            <Form.Item className={classnames('acf-form-override')} label="Override">
                                                <div className="switchable-input">
                                                    <Switch
                                                        checked={overrideEnabled}
                                                        onChange={toggleOverrideEnabled}
                                                    />

                                                    {overrideEnabled && (
                                                        <Form.Item
                                                            name="override"
                                                            initialValue={expectedChargeValue}
                                                            noStyle
                                                        >
                                                            <InputNumber
                                                                min={1}
                                                                formatter={(v) =>
                                                                    `${currentCurrencySymbol} ${v}`.replace(
                                                                        /\B(?=(\d{3})+(?!\d))/g,
                                                                        ',',
                                                                    )
                                                                }
                                                            />
                                                        </Form.Item>
                                                    )}
                                                </div>
                                            </Form.Item>

                                            {overrideEnabled && (
                                                <>
                                                    <Form.Item
                                                        className={classnames('acf-form-lid-override')}
                                                        label="Description"
                                                        name="lidOverride"
                                                        initialValue={lineItemDescription}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message:
                                                                    'A description must be provided for charge overrides.',
                                                            },
                                                        ]}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            )}

                                            <Form.Item
                                                className={classnames('acf-form-notes')}
                                                label="Notes"
                                                name="notes"
                                                rules={[
                                                    {
                                                        required: overrideEnabled,
                                                        message: 'A reason must be provided for charge overrides.',
                                                    },
                                                ]}
                                            >
                                                <Input.TextArea rows={2} autoSize={false} />
                                            </Form.Item>
                                        </>
                                    )}
                                </>
                            )}
                        </Form>
                    </Well>
                </>
            )}
        </Drawer>
    )
}
